import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MapLocation } from '@repo/shared';
import { CoverageNotice } from '../components/CoverageNotice';
import { LocateButton } from '../components/LocateButton';
import { MapView, type MapTarget, type MapViewport } from '../components/MapView';
import { RadarControls } from '../components/RadarControls';
import { RadarTimeline } from '../components/RadarTimeline';
import { SavedLocations } from '../components/SavedLocations';
import { SearchBox } from '../components/SearchBox';
import { SearchHistory } from '../components/SearchHistory';
import { useAppConfig } from '../features/config/hooks';
import { useGeolocation } from '../features/location/useGeolocation';
import { useRadarFrames } from '../features/radar/hooks';
import { useRadarAnimation } from '../features/radar/useRadarAnimation';
import { availableBasemaps } from '../lib/basemap';
import { type CoverageGap, coverageGapAt } from '../lib/coverage-gaps';
import { tileOptionsFrom, usePreferences } from '../lib/preferences';
import { useSavedLocations } from '../lib/saved-locations';
import { useSearchHistory } from '../lib/search-history';
import { hasViewportInUrl, readViewportFromUrl, writeViewportToUrl } from '../lib/viewport-url';
import styles from './RadarPage.module.css';

/**
 * The whole app: a full-bleed radar map with floating controls.
 *
 * State is deliberately shallow. The map owns its own camera, the animation
 * hook owns which frame is showing, the preferences hook owns how everything is
 * drawn, and this component only holds the handful of values they need to agree
 * on.
 */
export function RadarPage() {
  const config = useAppConfig();
  const radar = useRadarFrames();
  const { preferences, update, setBasemap } = usePreferences();

  const frames = useMemo(() => radar.data?.frames ?? [], [radar.data]);
  const animation = useRadarAnimation(frames.length);

  const [target, setTarget] = useState<MapTarget | null>(null);
  const saved = useSavedLocations();
  const history = useSearchHistory();

  const mapboxToken = config.data?.mapboxToken ?? '';
  const basemaps = useMemo(() => availableBasemaps(mapboxToken), [mapboxToken]);

  // A new object identity here remounts every radar layer, since the options
  // are baked into each tile URL — so it's memoised on the preferences object.
  const tileOptions = useMemo(() => tileOptionsFrom(preferences), [preferences]);

  // Where the map opens, resolved once from the URL so a shared link restores
  // the same view. Never updated — the map owns its camera after mount.
  const [initialViewport] = useState(() => readViewportFromUrl(window.location.search));

  // The map's live position, kept in a ref: it changes on every pan frame and
  // nothing renders from it directly. Search proximity reads it on demand.
  const viewportRef = useRef<MapViewport>(initialViewport);

  /*
   * Whether the view is sitting in a region RainViewer claims and doesn't
   * deliver — see lib/coverage-gaps.ts.
   *
   * State, unlike the viewport itself, because the notice renders from it. It
   * only changes on crossing a boundary, so the map can be panned all day
   * without re-rendering this page: `coverageGapAt` returns entries from a fixed
   * list, so staying put resolves to the identical object.
   */
  const [coverageGap, setCoverageGap] = useState<CoverageGap | null>(() =>
    coverageGapAt(initialViewport.longitude, initialViewport.latitude),
  );
  const [dismissedGapId, setDismissedGapId] = useState<string | null>(null);

  const onViewportChange = useCallback((viewport: MapViewport) => {
    viewportRef.current = viewport;
    writeViewportToUrl(viewport);
    setCoverageGap(coverageGapAt(viewport.longitude, viewport.latitude));
  }, []);

  const goTo = useCallback((location: MapTarget) => setTarget(location), []);

  // A device fix is marked differently from a searched address: one is a
  // measurement, the other is a name someone typed. See lib/map-markers.ts.
  const onLocated = useCallback(
    (location: MapLocation) => setTarget({ ...location, kind: 'located' }),
    [],
  );
  const geolocation = useGeolocation(onLocated);

  // Offer to open on the viewer's own location, but only when they arrived
  // without one. A URL carrying `lon`/`lat` is someone following a link to a
  // specific place, and yanking the map to wherever they happen to be standing
  // would discard the thing they were sent to look at.
  //
  // `request` is stable and this runs once. A denial is not retried — the hook
  // surfaces the error and the viewer can use the button or search instead.
  const requestLocation = geolocation.request;
  useEffect(() => {
    if (hasViewportInUrl(window.location.search)) return;
    requestLocation();
  }, [requestLocation]);

  // Transport shortcuts, for a map you look at rather than click on. Ignored
  // while typing; the panel handle stops its own arrow keys from reaching here.
  const { toggle, step } = animation;
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isOwnedByAControl(event.target)) return;

      if (event.key === ' ') {
        event.preventDefault();
        toggle();
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        step(1);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [step, toggle]);

  // Bias search toward what's on screen, rounded so the query key stays stable
  // across small pans and keeps hitting the cache.
  const proximity = `${viewportRef.current.longitude.toFixed(2)},${viewportRef.current.latitude.toFixed(2)}`;

  const currentLocation: MapLocation | null = target
    ? { label: target.label, longitude: target.longitude, latitude: target.latitude }
    : null;

  return (
    <div className={styles.shell}>
      <header className={styles.header}>
        <span className={styles.brand}>
          <SweepMark />
          <span className={styles.wordmark}>
            ray<span className={styles.brandAccent}>dario</span>
          </span>
        </span>

        <SearchBox
          enabled={config.data?.searchEnabled ?? false}
          proximity={proximity}
          onSelect={(place) => {
            const searched = {
              label: place.label,
              longitude: place.longitude,
              latitude: place.latitude,
              bbox: place.bbox,
            };
            // Recorded as a place, without how it was arrived at: `kind` is a
            // marker concern, and the history stores somewhere you went.
            history.record(searched);
            goTo({ ...searched, kind: 'search' });
          }}
        />

        <div className={styles.headerActions}>
          <LocateButton status={geolocation.status} onClick={geolocation.request} />

          <SearchHistory
            entries={history.entries}
            onSelect={goTo}
            onRemove={history.remove}
            onClear={history.clear}
          />

          <SavedLocations
            locations={saved.locations}
            current={currentLocation}
            isCurrentSaved={saved.isSaved(currentLocation)}
            onSelect={goTo}
            onSave={saved.save}
            onRemove={saved.remove}
          />
        </div>
      </header>

      <main className={styles.stage}>
        {config.isPending ? (
          <p className={styles.status}>Loading map…</p>
        ) : config.isError ? (
          <p className={styles.status}>Could not load configuration. Is the Worker running?</p>
        ) : (
          <MapView
            mapboxToken={config.data.mapboxToken}
            basemapId={preferences.basemap}
            basemapDim={preferences.basemapDim}
            initialViewport={initialViewport}
            frames={frames}
            activeFrameIndex={animation.index}
            radarOpacity={preferences.radarOpacity}
            tileOptions={tileOptions}
            showCoverage={preferences.showCoverage}
            target={target}
            savedLocations={saved.locations}
            onSelectSaved={goTo}
            onViewportChange={onViewportChange}
          />
        )}

        {geolocation.error && (
          <p className={styles.toast} role="status">
            {geolocation.error}
          </p>
        )}

        {/*
          Overlay chrome sits above the map but must not swallow map
          interaction, so the container is click-through and each child opts
          back in.
        */}
        <div className={styles.overlay}>
          <CoverageNotice
            gap={coverageGap?.id === dismissedGapId ? null : coverageGap}
            onDismiss={() => setDismissedGapId(coverageGap?.id ?? null)}
          />

          {config.data && (
            <RadarControls
              preferences={preferences}
              basemaps={basemaps}
              onChange={update}
              onBasemapChange={setBasemap}
            />
          )}

          <div className={styles.timeline}>
            {radar.isError ? (
              <p className={styles.toast} role="status">
                Radar is unavailable right now. Retrying…
              </p>
            ) : frames.length === 0 ? (
              <p className={styles.toast} role="status">
                Loading radar frames…
              </p>
            ) : (
              <RadarTimeline
                frames={frames}
                animation={animation}
                referenceTime={radar.data?.generated ?? frames[frames.length - 1]!.time}
              />
            )}
          </div>
        </div>
      </main>

      {/*
        Two tokens, two banners. The basemap and the search each run on their
        own Mapbox credential, so either can be missing alone — naming the
        wrong one sends someone to fix a variable that was already set.
      */}
      {config.data && !config.data.mapboxToken && (
        <p className={styles.banner}>
          Running on the keyless basemap. Set <code>MAPBOX_PUBLIC_TOKEN</code> in{' '}
          <code>apps/server/.dev.vars</code> to enable the Mapbox basemaps.
        </p>
      )}

      {config.data && !config.data.searchEnabled && (
        <p className={styles.banner}>
          Address search is off. Set <code>MAPBOX_SECRET_TOKEN</code> in{' '}
          <code>apps/server/.dev.vars</code> to enable it.
        </p>
      )}
    </div>
  );
}

/**
 * True for anything that wants the key press more than the map does.
 *
 * Buttons count: space activates a focused button, and without this the play
 * button would toggle playback twice — once itself, once here — and appear to
 * do nothing at all.
 */
function isOwnedByAControl(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;

  return ['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON', 'SUMMARY', 'A'].includes(target.tagName);
}

/** The brand mark: a radar sweep over two range rings. */
function SweepMark() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" className={styles.mark} aria-hidden="true">
      <defs>
        <linearGradient id="raydario-sweep" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%" stopColor="currentColor" stopOpacity="0" />
          <stop offset="100%" stopColor="currentColor" stopOpacity="0.8" />
        </linearGradient>
      </defs>
      <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeOpacity="0.3" />
      <circle cx="12" cy="12" r="5.5" fill="none" stroke="currentColor" strokeOpacity="0.3" />
      <path
        className={styles.sweep}
        d="M12 12 L12 2 A10 10 0 0 1 19.1 4.9 Z"
        fill="url(#raydario-sweep)"
      />
      <circle cx="12" cy="12" r="1.4" fill="currentColor" />
    </svg>
  );
}
